import React from 'react';
import { PerformanceReviewEvidenceHighlight, PerformanceReviewStudentSelfReview } from '../../types/v2';

type StudentAssessment = {
  focus: 'strength' | 'growth';
  label: string;
  level: string;
  evidence: string;
};

type TeacherArea = {
  key: string;
  label: string;
  level: string;
  comment?: string;
};

type TeacherReview = {
  goalOutcome: string;
  technicalEvidence?: string;
  goalOutcomeNote?: string;
  areas: TeacherArea[];
  overallSummary?: string;
  industryFeedback?: string;
  nextSteps: Array<{ label: string; value?: string }>;
};

type Props = {
  selfReview: PerformanceReviewStudentSelfReview;
  studentAssessments: StudentAssessment[];
  teacherReview: TeacherReview;
  goal?: { title?: string; target?: string } | null;
  evidenceHighlight?: PerformanceReviewEvidenceHighlight | null;
  workHabits?: { dailyGoalsRecorded: number; dailyWrapupsCompleted: number } | null;
};

const responseRows: Array<{ key: keyof PerformanceReviewStudentSelfReview; label: string }> = [
  { key: 'accomplishment_text', label: 'Results and impact' },
  { key: 'strongest_work_text', label: 'Evidence of quality or growth' },
  { key: 'challenge_text', label: 'Problem-solving, feedback, and revision' },
  { key: 'skill_growth_text', label: 'Professional practice' },
  { key: 'next_direction_text', label: 'Next-cycle plan' },
];

export const SharedReviewComparison: React.FC<Props> = ({ selfReview, studentAssessments, teacherReview, goal, evidenceHighlight, workHabits }) => {
  const teacherPanel = <article aria-label="Teacher review" className="rounded-xl border border-brand/25 bg-brand/5 p-5">
    <p className="text-xs font-semibold uppercase tracking-widest text-brand">Teacher review</p>
    <h3 className="mt-2 text-lg font-semibold text-text">Feedback and next steps</h3>
    <dl className="mt-4 space-y-3 text-sm">
      <div><dt className="font-semibold text-text">Goal outcome</dt><dd className="mt-1 capitalize text-muted">{teacherReview.goalOutcome.replace(/_/g, ' ') || 'Not selected'}</dd></div>
      {teacherReview.technicalEvidence !== undefined && <div><dt className="font-semibold text-text">Technical evidence</dt><dd className="mt-1 capitalize text-muted">{teacherReview.technicalEvidence.replace(/_/g, ' ') || 'Not selected'}</dd></div>}
      {teacherReview.goalOutcomeNote && <div><dt className="font-semibold text-text">Goal-outcome explanation</dt><dd className="mt-1 whitespace-pre-wrap text-muted">{teacherReview.goalOutcomeNote}</dd></div>}
    </dl>
    {teacherReview.areas.length > 0 && <div className="mt-5 border-t border-brand/20 pt-4">
      <h4 className="font-semibold text-text">Performance areas</h4>
      <div className="mt-3 space-y-3">{teacherReview.areas.map((area) => <section key={area.key} className="rounded-lg bg-surface p-3 text-sm">
        <h5 className="font-semibold text-text">{area.label} · {area.level}</h5>
        {area.comment && <p className="mt-1 whitespace-pre-wrap text-muted">{area.comment}</p>}
      </section>)}</div>
    </div>}
    <div className="mt-5 space-y-4 border-t border-brand/20 pt-4 text-sm">
      <div><h4 className="font-semibold text-text">Overall shared summary</h4><p className="mt-1 whitespace-pre-wrap text-muted">{teacherReview.overallSummary || 'No summary recorded.'}</p></div>
      {teacherReview.industryFeedback && <div><h4 className="font-semibold text-text">Industry-specific feedback</h4><p className="mt-1 whitespace-pre-wrap text-muted">{teacherReview.industryFeedback}</p></div>}
      {teacherReview.nextSteps.filter((step) => step.value).map((step) => <div key={step.label}><h4 className="font-semibold text-text">{step.label}</h4><p className="mt-1 whitespace-pre-wrap text-muted">{step.value}</p></div>)}
    </div>
  </article>;

  const sharedContext = (goal?.title || goal?.target || evidenceHighlight || workHabits) && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    {(goal?.title || goal?.target) && <section aria-label="Goal for this review" className="rounded-xl border border-border bg-surface-alt/50 p-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">Goal for this review</p>
      {goal?.title && <h3 className="mt-2 text-lg font-semibold text-text">{goal.title}</h3>}
      {goal?.target && <p className="mt-2 whitespace-pre-wrap text-sm text-muted">{goal.target}</p>}
    </section>}
    {evidenceHighlight && <section aria-label="Latest evidence highlight" className="rounded-xl border border-border bg-surface-alt/50 p-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">Latest evidence highlight</p>
      <h3 className="mt-2 text-lg font-semibold text-text">{evidenceHighlight.title}</h3>
      <p className="mt-1 text-xs text-muted">{evidenceHighlight.source}{evidenceHighlight.date ? ` · ${evidenceHighlight.date}` : ''}{evidenceHighlight.verification ? ` · ${evidenceHighlight.verification}` : ''}</p>
      {evidenceHighlight.detail && <p className="mt-2 whitespace-pre-wrap text-sm text-muted">{evidenceHighlight.detail}</p>}
    </section>}
    {workHabits && <section aria-label="Work habits during this review" className="rounded-xl border border-border bg-surface-alt/50 p-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">Work habits during this review</p>
      <dl className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-surface p-3">
          <dt className="text-xs text-muted">Daily goals recorded</dt>
          <dd className="mt-1 text-2xl font-semibold text-text">{workHabits.dailyGoalsRecorded}</dd>
        </div>
        <div className="rounded-lg bg-surface p-3">
          <dt className="text-xs text-muted">Daily wrap-ups completed</dt>
          <dd className="mt-1 text-2xl font-semibold text-text">{workHabits.dailyWrapupsCompleted}</dd>
        </div>
      </dl>
    </section>}
  </div>;

  if (!selfReview.submitted) return <div className="space-y-5">{sharedContext}{teacherPanel}</div>;

  return <div className="space-y-5">{sharedContext}<div className="grid items-start gap-5 lg:grid-cols-2">
    <article aria-label="Student self-review" className="rounded-xl border border-border bg-surface p-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">Student self-review</p>
      <h3 className="mt-2 text-lg font-semibold text-text">My perspective</h3>
      <dl className="mt-4 space-y-4 text-sm">{responseRows.map(({ key, label }) => {
        const value = selfReview[key];
        return typeof value === 'string' && value ? <div key={key}><dt className="font-semibold text-text">{label}</dt><dd className="mt-1 whitespace-pre-wrap text-muted">{value}</dd></div> : null;
      })}</dl>
      {studentAssessments.length > 0 && <div className="mt-5 border-t border-border pt-4">
        <h4 className="font-semibold text-text">My self-assessment</h4>
        <div className="mt-3 space-y-3">{studentAssessments.map((assessment) => <section key={assessment.focus} className="rounded-lg bg-surface-alt/60 p-3 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">{assessment.focus === 'strength' ? 'Demonstrated strength' : 'Growth priority'}</p>
          <h5 className="mt-1 font-semibold text-text">{assessment.label} · {assessment.level}</h5>
          {assessment.evidence && <p className="mt-1 text-muted">{assessment.evidence}</p>}
        </section>)}</div>
      </div>}
    </article>
    {teacherPanel}
  </div></div>;
};
